import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { getMonthlyPnl, getMonthlyNetProfit, previousPeriod } from "../services/financeCore/monthlyPnlService";
import { getClientProfitabilityForPeriod } from "../services/financeCore/clientProfitabilityService";
import { getStatutoryControlSummary, recordStatutoryPayment } from "../services/financeCore/statutoryControlService";
import { DIRECT_COST_CATEGORIES, getDirectCostSummary, recordDirectCost } from "../services/financeCore/clientDirectCostService";
import { getGstPayableSummary, recordGstPaidByAlm } from "../services/financeCore/gstPayableService";
import { CLIENT_PICKER_LIST } from "../services/financeCore/clientTaxProfileService";
import { canReopenPeriods, isPeriodLocked, setPeriodLock } from "../services/financeCore/periodLockService";
import { auth } from "../firebase";

const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const compact = (value) => {
  const n = Math.round(Number(value || 0));
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};
const titleCase = (value) => String(value || "").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const monthLabel = (period) => {
  const [year, month] = String(period || "").split("-").map(Number);
  if (!year || !month) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

function Delta({ value }) {
  if (value == null) return <span className="pnl-kpi-delta flat">New this period</span>;
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const arrow = value > 0 ? "↑" : value < 0 ? "↓" : "→";
  return <span className={`pnl-kpi-delta ${dir}`}>{arrow} {Math.abs(value)}% vs prior month</span>;
}

function BarBreakdown({ rows, total, variant }) {
  const max = Math.max(1, ...rows.map((r) => r.amount));
  return (
    <div>
      {rows.map((row) => (
        <div className="pnl-bar-row" key={row.label}>
          <span className="pnl-bar-label">{row.label}</span>
          <span className="pnl-bar-track">
            <span className={`pnl-bar-fill ${variant || ""}`} style={{ width: `${Math.max(3, (row.amount / max) * 100)}%` }} />
          </span>
          <span className="pnl-bar-value">{compact(row.amount)}</span>
        </div>
      ))}
      {!rows.length && <p className="page-copy">No entries for this period.</p>}
      {!!rows.length && (
        <div className="pnl-total-row"><span>Total</span><strong>{money(total)}</strong></div>
      )}
    </div>
  );
}

function PnlTab({ period }) {
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMonthlyPnl(period).then(setPnl).finally(() => setLoading(false));
  }, [period]);

  if (loading || !pnl) return <div className="panel-body page-copy">Loading…</div>;

  const revenueRows = Object.entries(pnl.revenueByCategory)
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount]) => ({ label: titleCase(category), amount }));

  const expenseRows = Object.entries(pnl.operatingExpenses.byCategory)
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount]) => ({ label: titleCase(category), amount }));

  const now = new Date();

  return (
    <article className="pnl-report">
      <div className="pnl-letterhead">
        <div className="pnl-letterhead-brand">
          <img src="/alm-logo.png" alt="ALM logo" />
          <div>
            <h2 className="pnl-letterhead-title">ALM Outsourcing Services (Pvt) Ltd.</h2>
            <p className="pnl-letterhead-subtitle">Monthly Profit &amp; Loss Statement — Management Report</p>
          </div>
        </div>
        <div className="pnl-letterhead-meta">
          <strong>{monthLabel(period)}</strong><br />
          {pnl.invoiceCount} approved invoice(s) · {pnl.clientCount} client(s)<br />
          Generated {now.toLocaleDateString("en-GB")} by {auth.currentUser?.email || "—"}
        </div>
      </div>

      <div className="pnl-kpi-grid">
        <div className="pnl-kpi">
          <div className="pnl-kpi-label">Total Received from Client</div>
          <div className="pnl-kpi-value">{money(pnl.grossBilling)}</div>
          <span className="pnl-kpi-delta flat">Gross billing, incl. pass-through recoveries</span>
        </div>
        <div className="pnl-kpi">
          <div className="pnl-kpi-label">Direct Cost of Service</div>
          <div className="pnl-kpi-value">{money(pnl.directCost.total)}</div>
          <span className="pnl-kpi-delta flat">Salary/EOBI/PESSI paid for lump-sum clients (e.g. CBRE)</span>
        </div>
        <div className="pnl-kpi">
          <div className="pnl-kpi-label">Operating Expenses</div>
          <div className="pnl-kpi-value">{money(pnl.operatingExpenses.total)}</div>
          <span className="pnl-kpi-delta flat">Payroll + petty cash + manual</span>
        </div>
        <div className="pnl-kpi">
          <div className="pnl-kpi-label">Net Profit / (Loss)</div>
          <div className="pnl-kpi-value" style={{ color: pnl.operatingProfit >= 0 ? "var(--success)" : "var(--danger)" }}>
            {pnl.operatingProfit < 0 ? `(${money(Math.abs(pnl.operatingProfit))})` : money(pnl.operatingProfit)}
          </div>
          <Delta value={pnl.priorMonth.operatingProfitChange} />
        </div>
      </div>

      <div className="pnl-section">
        <h3 className="pnl-section-title">Revenue &amp; Expense Composition</h3>
        <p className="pnl-section-copy">
          Net service revenue (service charge, management fee, other revenue) drives margin. Gross billing of
          {" "}{money(pnl.grossBilling)} also includes pass-through recoveries (salary, EOBI, PESSI, GST) that ALM
          pays back out — shown in the category breakdown below for transparency, not counted as margin.
        </p>
        <div className="pnl-columns">
          <div>
            <h4 style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Revenue by category</h4>
            <BarBreakdown rows={revenueRows} total={pnl.grossBilling} />
          </div>
          <div>
            <h4 style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Operating expenses by category</h4>
            <BarBreakdown rows={expenseRows} total={pnl.operatingExpenses.total} variant="expense" />
          </div>
        </div>
      </div>

      <div className="pnl-section">
        <h3 className="pnl-section-title">Client Contribution</h3>
        <p className="pnl-section-copy">Top clients by net service revenue this period.</p>
        <table className="data-table">
          <thead><tr><th>Client</th><th>Invoices</th><th>Net Service Revenue</th><th>Gross Billing</th></tr></thead>
          <tbody>
            {pnl.topClients.map((c) => (
              <tr key={c.clientCode}>
                <td><strong>{c.clientCode}</strong></td><td>{c.invoiceCount}</td>
                <td>{money(c.netServiceRevenue)}</td><td>{money(c.grossBilling)}</td>
              </tr>
            ))}
            {!pnl.topClients.length && <tr><td colSpan={4}>No approved invoices for this period.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="pnl-section">
        <h3 className="pnl-section-title">GST Payable</h3>
        <table className="data-table">
          <tbody>
            <tr><td>GST carried forward from previous month(s)</td><td>{money(pnl.gstPayable.carriedForward)}</td></tr>
            <tr><td>GST payable this month (all clients)</td><td>{money(pnl.gstPayable.thisMonthPayable)}</td></tr>
            <tr><td>GST paid by ALM</td><td>{money(pnl.gstPayable.paidThisMonth)}</td></tr>
            <tr><td><strong>GST balance</strong></td><td><strong>{money(pnl.gstPayable.balance)}</strong></td></tr>
          </tbody>
        </table>
        <div className="pnl-note">
          Gross profit = net service revenue − direct cost of service − operating expenses. Pass-through recoveries
          (salary/EOBI/PESSI billed to clients as separate lines) are excluded from margin since ALM pays out the
          same amount it recovers for outsourced staff. For clients billed as a single lump amount with no recovery
          split (e.g. CBRE), that full amount lands in revenue as-is, so finance records the actual salary/EOBI/PESSI/
          other paid out under Direct Cost Control — that total is deducted here so it isn't mistaken for margin. GST
          is tracked as a separate running balance above, never as a revenue reduction or expense — record what ALM
          actually pays under GST Payable Control.
        </div>
      </div>

      <div className="pnl-footer pnl-print-hide">
        <span>Confidential — prepared for internal management review only. Not a statutory financial statement.</span>
        <button type="button" className="btn btn-muted" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
    </article>
  );
}

// Running record of each month's final bottom line — the classic
// "unappropriated profit and loss" ledger. Walks backward from the selected
// period, one month at a time, and keeps the months that actually had
// approved invoices (skipping quiet months rather than padding the table
// with zero rows) until it has gathered up to WINDOW_SIZE of them.
const WINDOW_SIZE = 12;
const MAX_LOOKBACK = 36;

function UnappropriatedPnlTab({ period }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const found = [];
      let cursor = period;
      for (let i = 0; i < MAX_LOOKBACK && found.length < WINDOW_SIZE; i += 1) {
        const result = await getMonthlyNetProfit(cursor);
        if (result.invoiceCount > 0) found.push(result);
        cursor = previousPeriod(cursor);
      }
      if (!cancelled) {
        setRows(found.reverse());
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">Unappropriated Profit &amp; Loss</h2>
        <p className="page-copy" style={{ margin: 0 }}>Final profit/(loss) recognized each month, most recent {WINDOW_SIZE} months with approved invoices, through {monthLabel(period)}.</p>
      </div>
      <div className="panel-body">
        {loading ? <p className="page-copy">Loading…</p> : (
          <table className="data-table">
            <thead><tr><th>Sr. No</th><th>Month</th><th>Amount</th></tr></thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.accountingPeriod}>
                  <td>{index + 1}</td>
                  <td>{monthLabel(row.accountingPeriod)}</td>
                  <td style={{ color: row.operatingProfit >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
                    {row.operatingProfit < 0 ? `(${money(Math.abs(row.operatingProfit))})` : money(row.operatingProfit)}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={3}>No months with approved invoices found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function ClientProfitabilityTab({ period }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getClientProfitabilityForPeriod(period).then(setRows).finally(() => setLoading(false));
  }, [period]);

  return (
    <section className="panel">
      <div className="panel-header"><h2 className="panel-title">Client Profitability — {period}</h2></div>
      <div className="panel-body">
        {loading ? <p className="page-copy">Loading…</p> : (
          <table className="data-table">
            <thead><tr><th>Client</th><th>Invoices</th><th>Gross Billing</th><th>Net Service Revenue</th><th>Direct Cost</th><th>Net of Direct Cost</th><th>Gross Invoiced</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.clientCode}>
                  <td>{row.clientCode}</td><td>{row.invoiceCount}</td>
                  <td>{money(row.grossBilling)}</td><td>{money(row.netServiceRevenue)}</td>
                  <td>{row.directCost ? money(row.directCost) : "—"}</td>
                  <td><strong>{money(row.netOfDirectCost)}</strong></td>
                  <td>{money(row.grossAmount)}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7}>No approved invoices for {period}.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function StatutoryTab({ period }) {
  const [type, setType] = useState("EOBI");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState({ amountPaid: "", paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: "" });
  const [message, setMessage] = useState("");

  const load = () => {
    setLoading(true);
    getStatutoryControlSummary(period, type).then(setSummary).finally(() => setLoading(false));
  };
  useEffect(load, [period, type]);

  const submitPayment = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      await recordStatutoryPayment({ type, accountingPeriod: period, ...payment });
      setMessage("Payment recorded.");
      setPayment({ amountPaid: "", paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: "" });
      load();
    } catch (err) {
      setMessage(err.message || "Could not record the payment.");
    }
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">EOBI / PESSI Control — {period}</h2>
        <div className="field" style={{ minWidth: 140 }}>
          <label>Type</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="EOBI">EOBI</option>
            <option value="PESSI">PESSI</option>
          </select>
        </div>
      </div>
      <div className="panel-body">
        {message && <p className="page-copy" role="status">{message}</p>}
        {type === "EOBI" && (
          <p className="page-copy" style={{ marginBottom: 16 }}>
            "Charged to clients" is the employer's 5% EOBI contribution only — the amount actually invoiced. The
            worker's own 1% is deducted from their salary, not billed to the client, but it is still remitted to
            EOBI. So "Paid to institution" (5% + 1% combined) will normally run <strong>higher</strong> than
            charged — that gap is expected, not a shortfall. Treat "Remaining payable" as informational only for
            EOBI; it doesn't isolate the employee-funded portion.
          </p>
        )}
        {loading || !summary ? <p className="page-copy">Loading…</p> : (
          <>
            <table className="data-table">
              <tbody>
                <tr><td>Charged to clients (recovered via invoices)</td><td>{money(summary.charged)}</td></tr>
                <tr><td>Paid to institution</td><td>{money(summary.paid)}</td></tr>
                <tr><td><strong>Remaining payable</strong></td><td><strong>{money(summary.remainingPayable)}</strong></td></tr>
              </tbody>
            </table>
            <h3 style={{ marginTop: 20 }}>Payments</h3>
            <table className="data-table">
              <thead><tr><th>Date</th><th>Amount</th><th>Reference</th></tr></thead>
              <tbody>
                {summary.payments.map((p) => <tr key={p.id}><td>{p.paymentDate}</td><td>{money(p.amountPaid)}</td><td>{p.referenceNumber}</td></tr>)}
                {!summary.payments.length && <tr><td colSpan={3}>No payments recorded yet.</td></tr>}
              </tbody>
            </table>
            <form className="form-grid" style={{ marginTop: 12 }} onSubmit={submitPayment}>
              <div className="field"><label>Amount Paid</label>
                <input className="input" type="number" value={payment.amountPaid} onChange={(e) => setPayment({ ...payment, amountPaid: e.target.value })} />
              </div>
              <div className="field"><label>Payment Date</label>
                <input className="input" type="date" value={payment.paymentDate} onChange={(e) => setPayment({ ...payment, paymentDate: e.target.value })} />
              </div>
              <div className="field"><label>Reference</label>
                <input className="input" value={payment.referenceNumber} onChange={(e) => setPayment({ ...payment, referenceNumber: e.target.value })} />
              </div>
              <div className="field" style={{ alignSelf: "end" }}>
                <button className="btn btn-primary" type="submit">Record Payment</button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

function DirectCostTab({ period }) {
  const [clientCode, setClientCode] = useState("CBRE");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState({
    category: "SALARY", amount: "", description: "",
    paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: ""
  });
  const [message, setMessage] = useState("");

  const load = () => {
    setLoading(true);
    getDirectCostSummary(clientCode, period).then(setSummary).finally(() => setLoading(false));
  };
  useEffect(load, [period, clientCode]);

  const submitEntry = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      await recordDirectCost({ clientCode, accountingPeriod: period, ...entry });
      setMessage("Cost recorded.");
      setEntry({ category: "SALARY", amount: "", description: "", paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: "" });
      load();
    } catch (err) {
      setMessage(err.message || "Could not record the cost.");
    }
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">Direct Cost Control — {period}</h2>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Client</label>
          <select className="input" value={clientCode} onChange={(e) => setClientCode(e.target.value)}>
            {CLIENT_PICKER_LIST.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div className="panel-body">
        <p className="page-copy" style={{ marginBottom: 16 }}>
          For clients whose invoice bills a single lump amount with no salary/EOBI/PESSI recovery split (e.g. CBRE),
          record what was actually paid out here each month. The total is deducted from net service revenue in the
          P&amp;L tab as Direct Cost of Service, so it isn't counted as margin.
        </p>
        {message && <p className="page-copy" role="status">{message}</p>}
        {loading || !summary ? <p className="page-copy">Loading…</p> : (
          <>
            <table className="data-table">
              <tbody>
                {DIRECT_COST_CATEGORIES.map((cat) => (
                  <tr key={cat}><td>{titleCase(cat)}</td><td>{money(summary.byCategory[cat] || 0)}</td></tr>
                ))}
                <tr><td><strong>Total</strong></td><td><strong>{money(summary.total)}</strong></td></tr>
              </tbody>
            </table>
            <h3 style={{ marginTop: 20 }}>Entries</h3>
            <table className="data-table">
              <thead><tr><th>Date</th><th>Category</th><th>Amount</th><th>Description</th><th>Reference</th></tr></thead>
              <tbody>
                {summary.entries.map((e) => (
                  <tr key={e.id}>
                    <td>{e.paymentDate}</td><td>{titleCase(e.category)}</td><td>{money(e.amount)}</td>
                    <td>{e.description}</td><td>{e.referenceNumber}</td>
                  </tr>
                ))}
                {!summary.entries.length && <tr><td colSpan={5}>No entries recorded yet.</td></tr>}
              </tbody>
            </table>
            <form className="form-grid" style={{ marginTop: 12 }} onSubmit={submitEntry}>
              <div className="field"><label>Category</label>
                <select className="input" value={entry.category} onChange={(e) => setEntry({ ...entry, category: e.target.value })}>
                  {DIRECT_COST_CATEGORIES.map((cat) => <option key={cat} value={cat}>{titleCase(cat)}</option>)}
                </select>
              </div>
              <div className="field"><label>Amount Paid</label>
                <input className="input" type="number" value={entry.amount} onChange={(e) => setEntry({ ...entry, amount: e.target.value })} />
              </div>
              <div className="field"><label>Payment Date</label>
                <input className="input" type="date" value={entry.paymentDate} onChange={(e) => setEntry({ ...entry, paymentDate: e.target.value })} />
              </div>
              <div className="field"><label>Description</label>
                <input className="input" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} />
              </div>
              <div className="field"><label>Reference</label>
                <input className="input" value={entry.referenceNumber} onChange={(e) => setEntry({ ...entry, referenceNumber: e.target.value })} />
              </div>
              <div className="field" style={{ alignSelf: "end" }}>
                <button className="btn btn-primary" type="submit">Record Cost</button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

function GstPayableTab({ period }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState({
    amount: "", description: "", paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: ""
  });
  const [message, setMessage] = useState("");

  const load = () => {
    setLoading(true);
    getGstPayableSummary(period).then(setSummary).finally(() => setLoading(false));
  };
  useEffect(load, [period]);

  const submitEntry = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      await recordGstPaidByAlm({ monthKey: period, ...entry });
      setMessage("Payment recorded.");
      setEntry({ amount: "", description: "", paymentDate: new Date().toISOString().slice(0, 10), referenceNumber: "" });
      load();
    } catch (err) {
      setMessage(err.message || "Could not record the payment.");
    }
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">GST Payable Control — {period}</h2>
      </div>
      <div className="panel-body">
        <p className="page-copy" style={{ marginBottom: 16 }}>
          Record what ALM actually pays toward GST each month here. The running balance (carried forward from all
          prior months + this month's output GST − what's been paid) shows on the P&amp;L tab as GST Payable.
        </p>
        {message && <p className="page-copy" role="status">{message}</p>}
        {loading || !summary ? <p className="page-copy">Loading…</p> : (
          <>
            <table className="data-table">
              <tbody>
                <tr><td>GST carried forward from previous month(s)</td><td>{money(summary.carriedForward)}</td></tr>
                <tr><td>GST payable this month (all clients)</td><td>{money(summary.thisMonthPayable)}</td></tr>
                <tr><td>GST paid by ALM</td><td>{money(summary.paidThisMonth)}</td></tr>
                <tr><td><strong>GST balance</strong></td><td><strong>{money(summary.balance)}</strong></td></tr>
              </tbody>
            </table>
            <h3 style={{ marginTop: 20 }}>Payments</h3>
            <table className="data-table">
              <thead><tr><th>Date</th><th>Amount</th><th>Description</th><th>Reference</th></tr></thead>
              <tbody>
                {summary.paidEntries.map((e) => (
                  <tr key={e.id}><td>{e.paymentDate}</td><td>{money(e.amount)}</td><td>{e.description}</td><td>{e.referenceNumber}</td></tr>
                ))}
                {!summary.paidEntries.length && <tr><td colSpan={4}>No payments recorded yet.</td></tr>}
              </tbody>
            </table>
            <form className="form-grid" style={{ marginTop: 12 }} onSubmit={submitEntry}>
              <div className="field"><label>Amount Paid</label>
                <input className="input" type="number" value={entry.amount} onChange={(e) => setEntry({ ...entry, amount: e.target.value })} />
              </div>
              <div className="field"><label>Payment Date</label>
                <input className="input" type="date" value={entry.paymentDate} onChange={(e) => setEntry({ ...entry, paymentDate: e.target.value })} />
              </div>
              <div className="field"><label>Description</label>
                <input className="input" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} />
              </div>
              <div className="field"><label>Reference</label>
                <input className="input" value={entry.referenceNumber} onChange={(e) => setEntry({ ...entry, referenceNumber: e.target.value })} />
              </div>
              <div className="field" style={{ alignSelf: "end" }}>
                <button className="btn btn-primary" type="submit">Record Payment</button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

// Closing a month freezes it: no invoice, direct cost, expense, petty cash or payroll
// record can be added, edited or reversed in it until it is reopened (admin only).
function PeriodLockBar({ period, onChange }) {
  const [locked, setLocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLocked(null); setError("");
    isPeriodLocked(period).then(setLocked).catch(() => setLocked(false));
  }, [period]);

  const toggle = async () => {
    setError("");
    let note = "";
    if (locked) {
      note = window.prompt(`Reason for reopening ${period}?`) || "";
      if (!note.trim()) return;
    } else if (!window.confirm(`Close ${period}? No invoice, direct cost, expense, petty cash or payroll entry can be added, edited or reversed in this month until an admin reopens it.`)) {
      return;
    }
    setBusy(true);
    try {
      await setPeriodLock(period, !locked, note);
      setLocked(!locked);
      onChange?.();
    } catch (err) {
      setError(err.message || "The month could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  if (locked == null) return null;
  const mayToggle = !locked || canReopenPeriods();
  return (
    <div className={`management-alert ${locked ? "error" : "success"}`} style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <span>
        <strong>{locked ? `${period} is closed.` : `${period} is open.`}</strong>{" "}
        {locked ? "Nothing that moves this month's P&L can be added, edited or reversed. Corrections go in the next open month." : "Close it once the month is final to freeze its P&L."}
        {error && <span style={{ color: "#c0392b" }}> {error}</span>}
      </span>
      {mayToggle && <button className="btn btn-muted" type="button" disabled={busy} onClick={toggle}>{locked ? "Reopen month" : "Close month"}</button>}
    </div>
  );
}

export default function MonthlyPnl() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState("PNL");

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Monthly P&amp;L</h1>
            <p className="page-copy">Revenue, operating expenses, and statutory control, computed fresh from the underlying records every time.</p>
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <label>Period</label>
            <input className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </div>
        </header>
        <div className="button-row" style={{ margin: 0, borderTop: 0, padding: "0 0 18px" }}>
          <button className={`btn ${tab === "PNL" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("PNL")}>P&amp;L</button>
          <button className={`btn ${tab === "PROFITABILITY" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("PROFITABILITY")}>Client Profitability</button>
          <button className={`btn ${tab === "STATUTORY" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("STATUTORY")}>EOBI/PESSI Control</button>
          <button className={`btn ${tab === "DIRECT_COST" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("DIRECT_COST")}>Direct Cost Control</button>
          <button className={`btn ${tab === "GST_PAYABLE" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("GST_PAYABLE")}>GST Payable Control</button>
          <button className={`btn ${tab === "UNAPPROPRIATED" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("UNAPPROPRIATED")}>Unappropriated P&amp;L</button>
        </div>
        <PeriodLockBar period={period} />
        {tab === "PNL" && <PnlTab period={period} />}
        {tab === "PROFITABILITY" && <ClientProfitabilityTab period={period} />}
        {tab === "STATUTORY" && <StatutoryTab period={period} />}
        {tab === "DIRECT_COST" && <DirectCostTab period={period} />}
        {tab === "GST_PAYABLE" && <GstPayableTab period={period} />}
        {tab === "UNAPPROPRIATED" && <UnappropriatedPnlTab period={period} />}
      </main>
    </div>
  );
}
